/**
 * THE ENGINE FEED: turning stream events into the lines a viewer reads.
 *
 * <h2>Why this is not inside the component</h2>
 *
 * The HUD is the part of the demo people actually watch, and every interesting property of it is
 * about CONTENT, not about pixels — that a rejection is red, that an entry names its share count,
 * that a bar arriving while flat does not claim a position. jsdom cannot render a chart canvas, so
 * a component test would have to mock the chart away and would then be testing the mock. These
 * functions have no DOM, so they are tested directly.
 *
 * <h2>The palette is semantic, not decorative</h2>
 *
 * Five severities, each meaning one thing. A viewer scanning a scrolling feed reads colour before
 * text, so a colour that sometimes means "declined" and sometimes means "note" is worse than no
 * colour: it teaches a rule and then breaks it.
 */

/** Feed severities, and the colour each renders in. Dark-surface values — the HUD is glass. */
export const FEED_LEVELS = {
  MUTED: 'muted',
  INTENT: 'intent',
  REJECTED: 'rejected',
  ENTRY: 'entry',
  EXIT: 'exit',
  NOTE: 'note',
};

export const FEED_COLORS = {
  [FEED_LEVELS.MUTED]: 'rgba(255, 255, 255, 0.45)',
  [FEED_LEVELS.INTENT]: '#ffd54f',
  [FEED_LEVELS.REJECTED]: '#ff6b6b',
  [FEED_LEVELS.ENTRY]: '#4ade80',
  [FEED_LEVELS.EXIT]: '#ffa726',
  [FEED_LEVELS.NOTE]: 'rgba(255, 255, 255, 0.75)',
};

/**
 * How many lines the feed keeps.
 *
 * A stepped run streams up to 2,000 bars and every one produces a line, so an unbounded feed is a
 * 2,000-node list the browser re-lays-out on each append — the animation stutters exactly when the
 * demo is meant to look smooth. Trimming is the fix; the full history is in the final report.
 */
export const FEED_LIMIT = 300;

const dayOf = (iso) => (typeof iso === 'string' ? iso.slice(0, 10) : '—');
const timeOf = (iso) =>
  typeof iso === 'string' && iso.length >= 16 ? iso.slice(11, 16) : '';

/** Prices to two places for the feed — this is prose, not the ledger. */
const money = (value, places = 2) =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : value.toLocaleString('en-US', {
        minimumFractionDigits: places,
        maximumFractionDigits: places,
      });

/** Whole shares read as whole; a fractional size keeps enough places to be believed. */
const shares = (value) =>
  Number.isInteger(value) ? String(value) : money(value, 4);

let sequence = 0;
/** Monotonic ids. React needs a stable key, and two events can share a timestamp. */
const nextId = () => {
  sequence += 1;
  return sequence;
};

/** Exported only so a test can assert on ids without inheriting another test's counter. */
export const __resetFeedSequence = () => {
  sequence = 0;
};

const line = (level, text, detail) => ({ id: nextId(), level, text, detail });

/**
 * A bar, as the feed reports it.
 *
 * <p>Reported at MUTED always, including while a position is open: the bar is the heartbeat, and
 * promoting it whenever something is deployed would make three-quarters of a run bright.
 */
export const barLine = (bar) => {
  if (!bar) return null;
  const when = `${dayOf(bar.time)}${timeOf(bar.time) ? ` ${timeOf(bar.time)}` : ''}`;
  return line(
    FEED_LEVELS.MUTED,
    `Bar received: ${when} — ${money(bar.close)} — Engine in ${bar.state}`,
    bar.timeframe
  );
};

/**
 * An intent, coloured by what became of it.
 *
 * <p>A raised intent and a declined one are different findings and must not share a colour — on
 * SPY, 76 of 181 intents never became trades, so a feed that showed them all in yellow would
 * report the engine as far busier than it was.
 */
export const intentLine = (intent) => {
  if (!intent) return null;
  const shape = `${intent.signal}${intent.direction ? ` ${intent.direction}` : ''}`;
  const override = intent.bypassMacroVeto ? ' [macro veto bypassed]' : '';
  if (intent.outcome === 'TAKEN') {
    return line(FEED_LEVELS.INTENT, `Intent Generated: ${shape}${override}`, intent.provider);
  }
  const why =
    intent.outcome === 'DECLINED_POSITION_OPEN'
      ? 'a position is already open'
      : 'declined by the evaluator';
  return line(FEED_LEVELS.REJECTED, `Intent Declined: ${shape} — ${why}`, intent.provider);
};

/** A structural trigger the gates turned down. */
export const rejectionLine = (rejection) => {
  if (!rejection) return null;
  return line(
    FEED_LEVELS.REJECTED,
    `Intent Rejected: ${rejection.reason}`,
    `${dayOf(rejection.time)} · ${rejection.state || ''}`.trim()
  );
};

/**
 * A lifecycle change.
 *
 * <p>An ENTRY names the share count when the run is sized, because "IGNITION" alone does not say
 * whether anything was actually bought — an unfunded signal reaches the same state on the engine's
 * side and would read identically.
 *
 * <p>{@code position} defaults to the transition itself, which is where the server puts the size.
 * It cannot come from the latest bar: the engine announces a transition BEFORE the bar that
 * caused it, so on an entry the bar's account state is still the flat one.
 */
/**
 * THE BUY-BACK PREFIX, matched exactly as the Java side formats it.
 *
 * <p>A wire contract: {@code StructuralPremiseEvaluator.BUY_BACK_REASON}. It must not be shortened
 * to "RE-ENTRY", because the CTS second chance formats {@code "RE-ENTRY:"} and the two mechanics
 * have to stay distinguishable in a feed a viewer is auditing. {@code dynamanFeed.test.js} pins
 * this string against the Java source on disk.
 */
export const BUY_BACK_PREFIX = 'RE-ENTRY (buy-back):';

/**
 * THE RELOAD PREFIX, matched exactly as the Java side formats it.
 *
 * <p>A wire contract: {@code StructuralPremiseEvaluator.RELOAD_REASON}. It shares no prefix with
 * either of the other two re-entry mechanics — `RELOAD:` against `RE-ENTRY:` and
 * `RE-ENTRY (buy-back):` — so no `startsWith` test can capture a line belonging to another, and
 * the three stay separately readable in a feed somebody is auditing. Pinned against the Java
 * source on disk by `dynamanFeed.crosslang.test.js`.
 */
export const RELOAD_PREFIX = 'RELOAD:';

const isBuyBack = (reason) => typeof reason === 'string'
  && reason.startsWith(BUY_BACK_PREFIX);

const isReload = (reason) => typeof reason === 'string'
  && reason.startsWith(RELOAD_PREFIX);

export const transitionLine = (transition, position = transition) => {
  if (!transition) return null;
  const to = transition.to;
  const when = dayOf(transition.time);

  if (to === 'IGNITION') {
    const size =
      position && position.shares > 0
        ? `Bought ${shares(position.shares)} shares at ${money(position.entryFill)}`
        : 'Position opened';
    // A BUY-BACK is named as one. It is the same premise retaken after a close re-claimed the band
    // line it had lost, and reading it as an ordinary IGNITION hides the single thing a viewer is
    // auditing -- that the engine came back to a trade it had been shaken out of. Keyed on the
    // reason prefix the evaluator formats, which is the wire contract TradeRecord classifies on.
    // THREE labels, because there are three ways into a premise and they are three different
    // findings. A RELOAD is a FRESH wave: the fan cut the previous one and price pulled back into
    // its own retracement band before turning, so unlike a buy-back it is not the same trade
    // retaken at the level it lost. Reading either as an ordinary IGNITION hides the one thing a
    // viewer watching this feed is there for.
    let label = 'IGNITION';
    if (isReload(transition.reason)) label = 'RELOAD';
    else if (isBuyBack(transition.reason)) label = 'RE-ENTRY';
    return line(FEED_LEVELS.ENTRY, `${label}: ${size}`, `${when} · ${transition.reason || ''}`.trim());
  }
  if (to === 'EXHAUSTION') {
    return line(
      FEED_LEVELS.EXIT,
      `EXHAUSTION: Position closed via ${exitRuleOf(transition.reason)}`,
      `${when} · ${transition.timeframe || ''}`.trim()
    );
  }
  return line(
    FEED_LEVELS.NOTE,
    `${transition.from} → ${to}`,
    `${when} · ${transition.reason || ''}`.trim()
  );
};

/**
 * The prefixes `StructuralPremiseEvaluator` formats its exit reasons with.
 *
 * <p>A cross-language contract: these are the literal values of `STOP_BREACH_REASON`,
 * `FLOOR_BROKEN_REASON` and `TRENDLINE_BROKEN_REASON`, which Java cannot hand to JavaScript.
 * `StructuralPremiseEvaluatorTest.theExitReasonPrefixesAreAWireContract` pins them on that side,
 * so a reworded message fails a Java test rather than silently reaching a viewer as raw prose.
 *
 * <p>Getting one wrong is not loud. `stop breached` was written here instead of
 * `structural stop breached`, the prefix never matched, and every stop-out in the demo rendered
 * as three lines of engine diagnostics inside the sentence "Position closed via …". Found by
 * reading the HUD on screen, not by a test — the test asserted the invented prefix.
 */
export const EXIT_REASON_PREFIXES = {
  // 'a resting stop', NOT 'the initial stop'. Under RATCHETING_GATES several gates move this one
  // level, so the breach message — which is worded identically however the level got there — can
  // no longer claim it was the entry stop. WHICH gate owned it is in the ledger's EXIT REASON
  // column (BREAKEVEN_STOP / MFE_TRAIL / TRENDLINE_STOP), because only the ledger watched the
  // stop move; the rules are stateless and the transition prose does not carry it.
  // THE TWO PROFIT-PROTECTION GATES, listed BEFORE the generic breach line. Both exit through
  // the same "structural stop breached" prose, so the ledger's EXIT REASON column is where the
  // owning gate is named -- but when the engine DOES name one in a stop-move reason, that is
  // worth saying rather than falling through to "a resting stop".
  'breakeven lock': 'BREAKEVEN LOCK HIT',
  'accelerated ratchet': 'ACCELERATED RATCHET',
  'structural stop breached': 'a resting stop',
  'dynamic floor broken': 'ATR_TRAILING_FLOOR',
  'swing trendline broken': 'DYNAMIC_TRENDLINE_BREAK',
  // ADVANCED TRADE MANAGEMENT. 'accelerating fan broken' is listed as its own entry rather than
  // folded into the swing-trendline line above: both are geometric closes, and the whole reason
  // the fan exists is that it is anchored at the ENTRY and the trendline is not.
  'accelerating fan broken': 'the accelerating fan',
  // 'time stop' is NOT a prefix of 'trendline stop' in either direction, so the two cannot
  // capture each other however this map is reordered.
  'time stop': 'the time limit (breakeven never reached)',
  'atr trailing stop': 'the ATR trail',
  // A new signal replaced the premise on this bar. Listed BEFORE the gate reasons and worded as a
  // decision rather than as a level, because nothing was resting at this price — it is the only
  // exit immediately followed by an entry on the same candle, which is what the chart shows.
  'reversed on a new signal': 'a reversing signal',
  // The profit targets. PTA is a SCALE-OUT and not an exit at all, so it is worded as one — it
  // reaches the feed on a hold, where the premise stays open.
  'PTA reached': 'the first profit target (half sold)',
  'PTB reached': 'the second profit target',
  // The gate reasons. They reach a feed line when a gate moves the stop on the same bar as a
  // phase change, where the evaluator appends them in brackets.
  'breakeven stop': 'the breakeven gate',
  'mfe trail': 'the excursion trail',
  'trendline stop': 'the trendline gate',
};

/**
 * The rule behind an exit, from the engine's own prose.
 *
 * <p>An unrecognised reason is passed through verbatim rather than labelled "unknown" — the same
 * choice `ExitReason.UNCLASSIFIED` makes on the backend, and for the same reason: a new rule
 * should look like a gap, not like a stop-out.
 */
export const exitRuleOf = (reason) => {
  if (!reason) return 'an unnamed rule';
  for (const [prefix, label] of Object.entries(EXIT_REASON_PREFIXES)) {
    if (reason.startsWith(prefix)) return label;
  }
  return reason;
};

/**
 * Appends lines, trimming the oldest past {@link FEED_LIMIT}.
 *
 * <p>Nulls are dropped rather than rendered, so a caller can pass the result of a line builder
 * straight in without checking it first.
 */
export const appendFeed = (feed, additions, limit = FEED_LIMIT) => {
  const incoming = (Array.isArray(additions) ? additions : [additions]).filter(Boolean);
  if (!incoming.length) return feed;
  const next = [...feed, ...incoming];
  return next.length > limit ? next.slice(next.length - limit) : next;
};

/** Candles for the chart, in the shape lightweight-charts wants. */
export const toCandle = (bar) => ({
  time: Math.floor(new Date(bar.time).getTime() / 1000),
  open: bar.open,
  high: bar.high,
  low: bar.low,
  close: bar.close,
});
